import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CollectionsIcon from '@mui/icons-material/Collections';

const GalleryManager = () => {
  const [gallery, setGallery] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [title, setTitle] = useState('');
  const [newImage, setNewImage] = useState('');
  const [preview, setPreview] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [albumTitle, setAlbumTitle] = useState('');
  const [writeUp, setWriteUp] = useState('');
  const [albumImages, setAlbumImages] = useState([]);
  const [publishAlbum, setPublishAlbum] = useState(true);
  const [savingAlbum, setSavingAlbum] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const token = localStorage.getItem('token');
  const apiBase = process.env.REACT_APP_API_BASE_URL || '';
  const authHeaders = { Authorization: `Bearer ${token}` };

  const albumPreviews = useMemo(
    () => albumImages.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    })),
    [albumImages]
  );

  useEffect(
    () => () => albumPreviews.forEach(({ url }) => URL.revokeObjectURL(url)),
    [albumPreviews]
  );

  const fetchGallery = () => {
    axios.get(`${apiBase}/api/admin/gallery`, { headers: authHeaders })
      .then((res) => setGallery(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('Fetch error:', err));
  };

  const fetchAlbums = () => {
    axios.get(`${apiBase}/api/admin/gallery-event-albums`, { headers: authHeaders })
      .then((res) => setAlbums(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error('Album fetch error:', err);
        setFeedback({ severity: 'error', text: 'Unable to load event albums.' });
      });
  };

  useEffect(() => {
    fetchGallery();
    fetchAlbums();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setNewImage(reader.result);
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAlbumFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;
    if (selected.length > 20) {
      setFeedback({ severity: 'warning', text: 'You can upload up to 20 images per event album.' });
      setAlbumImages(selected.slice(0, 20));
    } else {
      setAlbumImages(selected);
      setFeedback(null);
    }
    event.target.value = '';
  };

  const removeSelectedImage = (index) => {
    setAlbumImages((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleCreateAlbum = async () => {
    if (!albumTitle.trim() || !writeUp.trim() || !albumImages.length) {
      setFeedback({
        severity: 'warning',
        text: 'Add an event title, a short write-up, and at least one image.',
      });
      return;
    }

    const formData = new FormData();
    formData.append('title', albumTitle.trim());
    formData.append('writeUp', writeUp.trim());
    formData.append('published', String(publishAlbum));
    albumImages.forEach((file) => formData.append('images', file));

    setSavingAlbum(true);
    setFeedback(null);
    try {
      const response = await axios.post(
        `${apiBase}/api/admin/gallery-event-albums`,
        formData,
        { headers: authHeaders }
      );
      setAlbumTitle('');
      setWriteUp('');
      setAlbumImages([]);
      setPublishAlbum(true);
      setFeedback({
        severity: 'success',
        text: response.data?.message || 'Event album saved.',
      });
      fetchAlbums();
    } catch (error) {
      console.error('Album create failed:', error);
      setFeedback({
        severity: 'error',
        text: error.response?.data?.message || 'Failed to save the event album.',
      });
    } finally {
      setSavingAlbum(false);
    }
  };

  const toggleAlbumPublished = async (album) => {
    try {
      await axios.put(
        `${apiBase}/api/admin/gallery-event-albums/${album.id}`,
        { published: !album.published },
        { headers: authHeaders }
      );
      setFeedback({
        severity: 'success',
        text: !album.published ? 'Event album published.' : 'Event album unpublished.',
      });
      fetchAlbums();
    } catch (error) {
      console.error('Album publish update failed:', error);
      setFeedback({ severity: 'error', text: 'Unable to change the publication status.' });
    }
  };

  const deleteAlbum = async (album) => {
    if (!window.confirm(`Delete "${album.title}" and all ${album.image_count} images?`)) return;
    try {
      await axios.delete(
        `${apiBase}/api/admin/gallery-event-albums/${album.id}`,
        { headers: authHeaders }
      );
      setFeedback({ severity: 'success', text: 'Event album deleted.' });
      fetchAlbums();
    } catch (error) {
      console.error('Album delete failed:', error);
      setFeedback({ severity: 'error', text: 'Unable to delete the event album.' });
    }
  };

  const handleAddImage = async () => {
    if (!title || !newImage) {
      setFeedback({ severity: 'warning', text: 'Title and image are required.' });
      return;
    }

    try {
      await axios.post(
        `${apiBase}/api/admin/gallery`,
        { title, image: newImage, active: isActive },
        { headers: authHeaders }
      );
      setFeedback({ severity: 'success', text: 'Single image added to the legacy gallery.' });
      setTitle('');
      setNewImage('');
      setPreview('');
      setIsActive(true);
      fetchGallery();
    } catch (error) {
      console.error('Post failed:', error);
      setFeedback({ severity: 'error', text: 'Failed to add the image.' });
    }
  };

  const handleDelete = (id) => {
    axios.delete(`${apiBase}/api/admin/gallery/${id}`, { headers: authHeaders })
      .then(fetchGallery)
      .catch((err) => console.error('Delete error:', err));
  };

  const toggleActive = (id, currentStatus) => {
    axios.put(
      `${apiBase}/api/admin/gallery/${id}`,
      { active: !currentStatus },
      { headers: authHeaders }
    )
      .then(fetchGallery)
      .catch((err) => console.error('Toggle active error:', err));
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        Event galleries
      </Typography>
      <Typography color="text.secondary" mb={2.5}>
        Publish an event story with a short write-up and multiple photos.
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.severity}
          onClose={() => setFeedback(null)}
          sx={{ mb: 2 }}
        >
          {feedback.text}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
        {savingAlbum && <LinearProgress />}
        <CardContent>
          <Stack spacing={2}>
            <TextField
              label="Event title"
              value={albumTitle}
              onChange={(event) => setAlbumTitle(event.target.value)}
              inputProps={{ maxLength: 180 }}
              fullWidth
              required
            />
            <TextField
              label="Short write-up"
              value={writeUp}
              onChange={(event) => setWriteUp(event.target.value)}
              helperText={`${writeUp.length}/2000 characters`}
              inputProps={{ maxLength: 2000 }}
              multiline
              minRows={3}
              fullWidth
              required
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CollectionsIcon />}
                disabled={savingAlbum}
              >
                Select multiple images
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAlbumFiles}
                />
              </Button>
              <Chip
                label={`${albumImages.length} image${albumImages.length === 1 ? '' : 's'} selected`}
                color={albumImages.length ? 'primary' : 'default'}
                variant={albumImages.length ? 'filled' : 'outlined'}
              />
              <Stack direction="row" alignItems="center">
                <Typography>Publish now</Typography>
                <Switch
                  checked={publishAlbum}
                  onChange={(event) => setPublishAlbum(event.target.checked)}
                  disabled={savingAlbum}
                />
              </Stack>
              <Button
                variant="contained"
                onClick={handleCreateAlbum}
                disabled={savingAlbum}
                sx={{ ml: { sm: 'auto !important' } }}
              >
                {publishAlbum ? 'Publish event gallery' : 'Save unpublished'}
              </Button>
            </Stack>

            {albumPreviews.length > 0 && (
              <Grid container spacing={1.5}>
                {albumPreviews.map(({ file, url }, index) => (
                  <Grid item xs={6} sm={4} md={2} key={`${file.name}-${file.lastModified}`}>
                    <Box
                      sx={{
                        position: 'relative',
                        aspectRatio: '4 / 3',
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: 'grey.100',
                      }}
                    >
                      <Box
                        component="img"
                        src={url}
                        alt={file.name}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => removeSelectedImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'rgba(255,255,255,.92)',
                          '&:hover': { bgcolor: 'white' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h6" fontWeight={750} gutterBottom>
        Event albums
      </Typography>
      {albums.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No event albums have been created yet.
        </Alert>
      ) : (
        <Grid container spacing={2} mb={4}>
          {albums.map((album) => (
            <Grid item xs={12} sm={6} md={4} key={album.id}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
                {album.published && album.cover_image_id && (
                  <Box
                    component="img"
                    src={`${apiBase}/api/gallery-event-albums/images/${album.cover_image_id}`}
                    alt={album.title}
                    sx={{ width: '100%', height: 180, objectFit: 'cover' }}
                  />
                )}
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="subtitle1" fontWeight={800}>
                      {album.title}
                    </Typography>
                    <Chip
                      size="small"
                      color={album.published ? 'success' : 'default'}
                      label={album.published ? 'Published' : 'Unpublished'}
                    />
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {album.write_up}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                    {album.image_count} image{album.image_count === 1 ? '' : 's'} · Created by {album.created_by}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Switch
                    checked={album.published}
                    onChange={() => toggleAlbumPublished(album)}
                    inputProps={{ 'aria-label': `Publish ${album.title}` }}
                  />
                  <Typography variant="body2">
                    {album.published ? 'Visible publicly' : 'Hidden publicly'}
                  </Typography>
                  <IconButton
                    color="error"
                    onClick={() => deleteAlbum(album)}
                    aria-label={`Delete ${album.title}`}
                    sx={{ ml: 'auto' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Existing single-image gallery
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        These existing entries are unchanged and continue to work as before.
      </Typography>

      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          size="small"
        />
        <Button
          variant="contained"
          component="label"
          startIcon={<CloudUploadIcon />}
        >
          Upload image
          <input type="file" hidden accept="image/*" onChange={handleFileChange} />
        </Button>
        <Typography>Active:</Typography>
        <Switch
          checked={isActive}
          onChange={() => setIsActive(!isActive)}
          color="primary"
        />
        <Button onClick={handleAddImage} variant="contained" color="primary">
          Add to gallery
        </Button>
      </Box>

      {preview && (
        <Box mb={3}>
          <Typography variant="body2">Preview:</Typography>
          <img src={preview} alt="Preview" style={{ maxHeight: 100 }} />
        </Box>
      )}

      <Grid container spacing={2}>
        {gallery.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.id}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">
                  {item.title || 'Untitled'}
                </Typography>
                {item.image && (
                  <Box mt={1}>
                    <img
                      src={item.image}
                      alt={item.title}
                      style={{ maxWidth: '100%', maxHeight: 120 }}
                    />
                  </Box>
                )}
                <Typography variant="body2" mt={1}>
                  Created by: {item.created_by} <br />
                  Modified: {new Date(item.modified_date).toLocaleString()}
                </Typography>
              </CardContent>
              <CardActions>
                <Switch
                  checked={Boolean(item.active)}
                  onChange={() => toggleActive(item.id, item.active)}
                  color="primary"
                />
                <IconButton color="error" onClick={() => handleDelete(item.id)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default GalleryManager;
